import { useEffect, useState } from "react";
import { BellRing, Loader2, Radio, Tv, Link2, Send, Users, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

const PRESETS = [
  { kind: "radio", label: "Rádio ao vivo", icon: Radio, url: "/", title: "🔴 Estamos AO VIVO na Rádio!", body: "A Fala Itapira entrou no ar agora. Toque para ouvir." },
  { kind: "tv", label: "TV ao vivo", icon: Tv, url: "/tv", title: "🔴 Transmissão ao vivo na TV!", body: "Estamos transmitindo agora. Toque para assistir." },
  { kind: "custom", label: "Personalizado", icon: Link2, url: "/", title: "", body: "" },
];

const AdminNotifications = () => {
  const [form, setForm] = useState({ ...PRESETS[0] });
  const [stats, setStats] = useState({ subscribers: 0, history: [] });
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/admin/push/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const pick = (p) => setForm({ ...p });

  const send = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Preencha título e mensagem.");
      return;
    }
    if (!window.confirm(`Enviar este aviso para ${stats.subscribers} dispositivo(s) inscritos?`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/push/broadcast", {
        title: form.title, body: form.body, url: form.url || "/", kind: form.kind,
      });
      toast.success(`Aviso enviado para ${data.stats.sent} de ${data.stats.total} dispositivos.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">Push & Avisos</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">Notificações</h1>
        <p className="text-gray-500 mt-1">Avise ouvintes com som e alerta na barra quando a transmissão começar.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-[var(--fi-blue)] text-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.2)]" data-testid="push-subscribers-card">
          <div className="flex items-center gap-2 text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold"><Users className="w-4 h-4" /> Inscritos</div>
          <div className="font-display text-5xl font-black mt-1" data-testid="push-subscribers-count">{stats.subscribers}</div>
          <div className="text-white/60 text-xs mt-1">dispositivos com o sininho ativado</div>
        </div>
        <div className="sm:col-span-2 rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)] text-sm text-gray-600 leading-relaxed">
          <strong className="text-[var(--fi-blue)]">Como funciona:</strong> o aviso chega como notificação do sistema (Android, Windows, Mac e iPhone com o app instalado na tela inicial) e também dentro do app aberto, com som. Quem não ativou o sininho vê o alerta sonoro apenas com o app aberto.
        </div>
      </div>

      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">Enviar aviso agora</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.kind}
              type="button"
              onClick={() => pick(p)}
              data-testid={`push-preset-${p.kind}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition-colors ${
                form.kind === p.kind ? "bg-[var(--fi-blue)] text-white border-[var(--fi-blue)]" : "border-gray-200 text-gray-600 hover:border-[var(--fi-blue)]"
              }`}
            >
              <p.icon className="w-4 h-4" /> {p.label}
            </button>
          ))}
        </div>
        <form onSubmit={send} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">Título *</label>
            <input data-testid="push-title" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" placeholder="Ex: Estamos AO VIVO!" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">Mensagem *</label>
            <textarea data-testid="push-body" value={form.body} maxLength={400} rows={3} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input resize-none" placeholder="Texto curto que aparece na notificação" />
            <div className="text-xs text-gray-400 mt-1 text-right">{form.body.length}/400</div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">Abrir ao tocar</label>
            <select data-testid="push-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="input bg-white">
              <option value="/">Rádio ao vivo (início)</option>
              <option value="/tv">TV ao vivo</option>
              <option value="/musicas">Playlist musical</option>
              <option value="/instagram">Instagram</option>
              <option value="/contato">Painel do Cidadão</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <button type="submit" data-testid="push-send" disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar notificação
            </button>
          </div>
        </form>
        <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
      </section>

      <section>
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">Histórico</h2>
        <div className="space-y-2" data-testid="push-history">
          {stats.history.length === 0 && <div className="rounded-xl bg-white p-6 text-center text-gray-400">Nenhum aviso enviado ainda.</div>}
          {stats.history.map((n) => (
            <div key={n.id} data-testid={`push-history-${n.id}`} className="rounded-xl bg-white p-4 shadow-[0_6px_20px_rgba(13,27,75,0.05)] flex items-start gap-3">
              <BellRing className="w-5 h-5 text-[var(--fi-red)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[var(--fi-blue)] truncate">{n.title}</div>
                <div className="text-sm text-gray-600 line-clamp-2">{n.body}</div>
                <div className="text-[11px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString("pt-BR")} · abre {n.url}</div>
              </div>
              <div className="text-right text-xs font-bold shrink-0">
                <div className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> {n.stats?.sent ?? 0}</div>
                {(n.stats?.failed || 0) + (n.stats?.removed || 0) > 0 && (
                  <div className="inline-flex items-center gap-1 text-gray-400 ml-2"><XCircle className="w-3.5 h-3.5" /> {(n.stats?.failed || 0) + (n.stats?.removed || 0)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminNotifications;
